import React from "react";
import "./style.css";
import { ProgressCircle } from "../ProgressCircle";
import { useTranslation } from "react-i18next";
import { TimerControl } from "../../types/timer.type";
import { ExerciseTask } from "../../types/exercise.type";

interface HorTvbdProps {
  type?: ExerciseTask['type'];
  workoutName: string;
  totalTime: number;
  currentExercise: number;
  totalExercises: number;
  totalSets: number;
  hydrate_active: number; // Active hydration break index
  hydrate_total: number;  // Total number of hydration breaks
  showFinish?: boolean;
  finishMessage?: string;
  finishImage?: string;
  timerControlRef?: React.RefObject<TimerControl>;
  pod: number;
  totalPods: number;
  podDuration: number;
  currentSetTime: number;

}

export const HorTvbd: React.FC<HorTvbdProps> = ({
  totalTime = 60 * 42,
  currentExercise = 1,
  totalExercises = 1,
  showFinish = false,
  finishMessage = "finish.finish",
  finishImage = "/img/basezero_logo.png",
  timerControlRef,
  totalPods = 0,
  pod = 0,
  podDuration = 0,
  type = "work",
  currentSetTime = 0
}): React.JSX.Element => {

  const { t } = useTranslation();

  const podTimerRef = React.useRef<TimerControl>(null);
  const exerciseTimerRef = React.useRef<TimerControl>(null);
  const totalTimerRef = React.useRef<TimerControl>(null);

  // Create a proxy timer control that forwards actions to all timers
  const timerControlProxy: TimerControl = {
    play: () => {
      podTimerRef.current?.play();
      exerciseTimerRef.current?.play();
      totalTimerRef.current?.play();
    },
    stop: () => {
      podTimerRef.current?.stop();
      exerciseTimerRef.current?.stop();
      totalTimerRef.current?.stop();
    },
    resume: () => {
      podTimerRef.current?.resume();
      exerciseTimerRef.current?.resume();
      totalTimerRef.current?.resume();
    },
    reset: () => {
      podTimerRef.current?.reset();
      exerciseTimerRef.current?.reset();
      totalTimerRef.current?.reset();
    },
    setTime: (newTime: number) => {
      podTimerRef.current?.setTime(newTime);
      exerciseTimerRef.current?.setTime(newTime);
      totalTimerRef.current?.setTime(newTime);
    }
  };

  // Forward the proxy to the external ref
  React.useImperativeHandle(timerControlRef, () => timerControlProxy);

  const translatedFinishMessage = t(finishMessage);

  const generateSetProgress = () => {
    const progressPercentage = (currentExercise / totalExercises) * 100; // Calculate progress in percentage

    return (
      <div className="progress-bar-container">
        <div className="progress-filled" style={{ width: `calc(${progressPercentage}% - 125px - 40px)` }}></div>
        <div
          className="progress-indicator"
        >
          {currentExercise}/{totalExercises}
        </div>
        <div className="progress-remaining"></div>
      </div>
    );
  };

  return (
    <div className="hor-TVBD">
      {showFinish ? (
        <div className="finish-container">
          <img className="finish-gif" alt="Finish" src={finishImage} height={551} width={538} />
          <div className="finish-text">{translatedFinishMessage}</div>
        </div>
      ) : (<>
        <div className="progress-container">
          {generateSetProgress()}
        </div>
        <div className="timers-container">
          <div className="timer-container">
            <ProgressCircle
              type="solid"
              initialTotalTime={podDuration}
              ref={podTimerRef}
              label={t('pods')}
              icon="/img/cycle-white.svg"
              customValue={<>{pod} / {totalPods}</>}
            />
          </div>
          <div className="timer-container timer-exercises">
            <ProgressCircle
              // Ensures a re-render even if the setTime is the same
              key={`${currentSetTime}-${Date.now()}`}
              initialTotalTime={currentSetTime}
              ref={exerciseTimerRef}
              color={type == 'hydrate' || type == 'rest' ? 'red' : 'default'}
              label={type == 'hydrate' ? t('rest') : t(type)}
            />
          </div>
          <div className="timer-container">
            <ProgressCircle
              type="solid"
              initialTotalTime={totalTime}
              ref={totalTimerRef}
              label={t('total_time')}
              icon="/img/timer-white.svg"
            />
          </div>
        </div>
      </>
      )}

    </div>
  );
};